import { useCallback, useEffect, useRef, useState } from "react";
import { CreditCard, FileText, Lock, Settings2, PieChart } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatementsTab } from "@/components/cc/StatementsTab";
import { ReimburseTab } from "@/components/cc/ReimburseTab";
import { ReportsTab } from "@/components/cc/ReportsTab";
import { GmailSettingsTab } from "@/components/cc/GmailSettingsTab";
import { UploadDialog } from "@/components/cc/UploadDialog";
import { StatementDialog } from "@/components/cc/StatementDialog";

const tabCls = "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2";

export default function CreditCardToolPage() {
  const [tab, setTab] = useState("statements");
  const [statements, setStatements] = useState(null);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const loadStatements = useCallback(async () => {
    try {
      const { data } = await api.get("/cc/statements");
      setStatements(data);
      return data;
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat tagihan"));
      setStatements((s) => s || []);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);
  const loadReports = useCallback(async () => {
    try {
      const { data } = await api.get("/cc/reports");
      setReports(data);
    } catch (_) {
      setReports([]);
    }
  }, []);

  useEffect(() => {
    loadStatements();
    loadReports();
  }, [loadStatements, loadReports]);

  // Poll while AI is processing or a Gmail pull is running
  const processing = (statements || []).some((s) => s.status === "processing");
  const jobRunning = job?.status === "running";
  useEffect(() => {
    if (!processing && !jobRunning) return undefined;
    pollRef.current = setInterval(async () => {
      if (jobRunning) {
        try {
          const { data } = await api.get(`/cc/jobs/${job.id}`);
          setJob(data);
          if (data.status === "done") toast.success(data.message);
          if (data.status === "error") toast.error(data.message);
        } catch (_) {
          /* ignore */
        }
      }
      loadStatements();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [processing, jobRunning, job?.id, loadStatements]);

  // keep open detail in sync
  useEffect(() => {
    if (detail && statements) {
      const fresh = statements.find((s) => s.id === detail.id);
      if (fresh && fresh.updated_at !== detail.updated_at) setDetail(fresh);
    }
  }, [statements, detail]);

  const pull = async () => {
    try {
      const { data } = await api.post("/cc/gmail/pull");
      setJob(data);
      toast.info("Menarik email tagihan dari Gmail…");
    } catch (e) {
      toast.error(errMsg(e, "Gagal menarik email"));
      if (e?.response?.status === 400) setTab("gmail");
    }
  };

  const readyCount = (statements || []).filter((s) => s.status === "ready").length;

  return (
    <div className="max-w-[1500px] mx-auto space-y-6" data-testid="cc-page">
      <div>
        <div className="eyebrow flex items-center gap-2">
          <CreditCard className="h-3.5 w-3.5" /> Keuangan pribadi & reimburse
          <Badge variant="outline" className="border-[color:var(--hairline)] text-[10px] text-primary ml-1"><Lock className="h-3 w-3 mr-1" /> Super admin</Badge>
        </div>
        <h1 className="font-display text-4xl mt-1.5" data-testid="cc-title">Tagihan Kartu Kredit</h1>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl leading-relaxed">
          Tarik e-statement dari Gmail atau unggah banyak PDF sekaligus. AI merangkum transaksi per vendor dan kategori penggunaan, lalu Anda pilih mana yang diajukan sebagai reimburse ke perusahaan dalam bentuk PDF.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[hsl(220_12%_8%)] border border-border h-auto p-1 flex-wrap" data-testid="cc-tabs">
          <TabsTrigger value="statements" className={tabCls} data-testid="cc-tab-statements"><CreditCard className="h-4 w-4 mr-1.5" /> Tagihan</TabsTrigger>
          <TabsTrigger value="reimburse" className={tabCls} data-testid="cc-tab-reimburse">
            <PieChart className="h-4 w-4 mr-1.5" /> Ringkasan & Reimburse {readyCount > 0 && <span className="ml-1.5 text-[10px] num opacity-80">({readyCount})</span>}
          </TabsTrigger>
          <TabsTrigger value="reports" className={tabCls} data-testid="cc-tab-reports"><FileText className="h-4 w-4 mr-1.5" /> Laporan</TabsTrigger>
          <TabsTrigger value="gmail" className={tabCls} data-testid="cc-tab-gmail"><Settings2 className="h-4 w-4 mr-1.5" /> Pengaturan Gmail</TabsTrigger>
        </TabsList>

        <TabsContent value="statements" className="mt-5">
          <StatementsTab
            statements={statements}
            loading={loading}
            job={job}
            pulling={jobRunning}
            onPull={pull}
            onUpload={() => setUploadOpen(true)}
            onOpen={setDetail}
            onReload={loadStatements}
          />
        </TabsContent>
        <TabsContent value="reimburse" className="mt-5">
          <ReimburseTab
            statements={statements}
            onCreated={() => {
              loadStatements();
              loadReports();
            }}
          />
        </TabsContent>
        <TabsContent value="reports" className="mt-5">
          <ReportsTab
            reports={reports}
            onReload={() => {
              loadReports();
              loadStatements();
            }}
          />
        </TabsContent>
        <TabsContent value="gmail" className="mt-5">
          <GmailSettingsTab />
        </TabsContent>
      </Tabs>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={loadStatements} />
      <StatementDialog statement={detail} onClose={() => setDetail(null)} onChanged={loadStatements} />
    </div>
  );
}
